import React, { useState, useRef, useEffect } from 'react';
import { X, RefreshCw, Maximize, Minimize, Sun, Moon, Download, Clock } from 'lucide-react';

const LogViewerModal = ({ 
  isOpen, 
  onClose, 
  logSource, 
  title, 
  subtitle, 
  buildNumber, 
  timestamp,
  status
}) => {
  const [logs, setLogs] = useState('');
  const [darkMode, setDarkMode] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [logHeight, setLogHeight] = useState('h-96');
  const [lastRefreshTime, setLastRefreshTime] = useState(new Date());
  
  const logsContainerRef = useRef(null);
  const eventSourceRef = useRef(null);

  useEffect(() => {
    if (isOpen && logSource) {
      setLogs('');
      
      // Create a new EventSource for streaming logs
      const eventSource = new EventSource(logSource);
      eventSourceRef.current = eventSource;
      
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.status === 'BUILD_COMPLETED') {
            eventSource.close();
          } else if (data.log) {
            setLogs(prevLogs => prevLogs + '\n' + data.log);
            setLastRefreshTime(new Date());
          } else if (data.error) {
            console.error('Error:', data.error);
          }
        } catch (error) {
          console.error('Failed to parse event data:', error);
        }
      };
      
      eventSource.onerror = () => {
        eventSource.close();
      };
      
      // Clean up event source when modal closes
      return () => {
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
        }
      };
    }
  }, [isOpen, logSource]);

  // Handle escape key to exit full screen
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isFullScreen) {
        toggleFullScreen();
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [isFullScreen]);

  const toggleTheme = () => {
    setDarkMode(prev => !prev);
  };

  const toggleFullScreen = () => {
    setIsFullScreen(prev => !prev);
    setLogHeight(prev => prev === 'h-96' ? 'h-screen' : 'h-96');
  };

  const downloadLogs = () => {
    const logsText = logs;
    const blob = new Blob([logsText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `build-logs-${buildNumber}-${new Date().toISOString()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  const getStatusColor = () => {
    if (status === 'SUCCESS') return 'bg-green-500';
    if (status === 'FAILURE') return 'bg-red-500';
    return 'bg-yellow-500';
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm transition-opacity"></div>
      <div className="flex min-h-screen items-center justify-center p-4">
        <div 
          className={`
            relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700
            ${isFullScreen ? 'w-full h-full max-w-none' : 'w-full max-w-4xl'}
            transition-all duration-300 ease-in-out
          `}
        >
          <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2">
              {status && (
                <div className={`w-3 h-3 rounded-full ${getStatusColor()}`}></div>
              )}
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                {title || `Build #${buildNumber} Logs`}
              </h3>
              {timestamp && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {new Date(timestamp).toLocaleString()}
                </span>
              )}
              {subtitle && (
                <span className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {/* Last Refresh Time */}
              <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center mr-2">
                <Clock className="h-3 w-3 mr-1" />
                Last update: {lastRefreshTime.toLocaleTimeString()}
              </span>
              
              {/* Download Button */}
              <button
                onClick={downloadLogs}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Download logs"
              >
                <Download className="h-5 w-5" />
              </button>
              
              {/* Theme Toggle Button */}
              <button
                onClick={toggleTheme}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
              >
                {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>
              
              {/* Fullscreen Toggle Button */}
              <button
                onClick={toggleFullScreen}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title={isFullScreen ? "Exit full screen" : "Enter full screen"}
              >
                {isFullScreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
              </button>
              
              {/* Close Button */}
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          <div className="p-5">
            <pre 
              ref={logsContainerRef}
              className={`
                ${darkMode ? 'bg-gray-900 text-gray-200' : 'bg-gray-100 text-gray-800'} 
                p-5 rounded-lg overflow-auto ${isFullScreen ? 'h-screen max-h-screen' : logHeight} 
                font-mono text-sm leading-relaxed shadow-inner
              `}
            >
              {logs || 'Loading logs...'}
            </pre>
          </div>
          
          {!isFullScreen && (
            <div className="flex justify-end p-4 border-t bg-gray-50 dark:bg-gray-900 rounded-b-xl">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogViewerModal;